"use client";
import React, { useEffect, useState } from "react";
import "@/styles/components/tutorials.scss";

import Navbar from "@/components/common/Navbar";
import Footer from "@/components/common/Footer";
import Image from "next/image";

const tutorialsData = [
  {
    id: "get-started",
    title: "Get Started with Baskitt",
    items: [
      {
        id: "get-started-video",
        videoTitle: "How to Get Started on Baskitt",
        videoThumb: "/images/tutorials/get-started.png",
        content: [
          "Baskitt is your gateway to global shopping. Whether you're buying from the US, UK, or anywhere else in the world, we make it simple, fast, and affordable.",
          [
            "Download the Baskitt App from the App Store or Google Play.",
            "Sign up with your email address or social account.",
            "Verify your account via the confirmation email sent to your inbox.",
          ],
          "Once you're set up, you'll have access to your personal Baskitt dashboard where you can browse retailers, track shipments, and manage your wallet.",
          "Explore our curated list of supported international retailers and start adding items to your cart. Baskitt handles the rest — from checkout to your doorstep.",
          "If you need help at any stage, our support team is available 24/7 via in-app chat or email at support@baskitt.co.",
        ],
      },
    ],
  },
  {
    id: "create-account",
    title: "Create your user account",
    items: [
      {
        id: "create-account-video",
        videoTitle: "Account Setup Walkthrough",
        videoThumb: "/images/tutorials/create-account.png",
        content: [
          "Creating your Baskitt account takes less than two minutes. You'll need a valid email address and a strong password to get started.",
          [
            "Open the Baskitt app or visit www.baskitt.co.",
            "Tap 'Sign Up' and enter your full name, email, and password.",
            "Agree to the Terms of Service and Privacy Policy.",
            "Check your inbox for a verification email and click the confirmation link.",
          ],
          "After verification, complete your profile by adding your shipping address and preferred currency. This helps us deliver accurate shipping estimates right from the start.",
          "Enable two-factor authentication (2FA) in your security settings for an extra layer of protection on your account.",
        ],
      },
    ],
  },
  {
    id: "fund-wallet",
    title: "Fund your wallet and shop globally",
    items: [
      {
        id: "fund-wallet-video",
        videoTitle: "Funding Your Baskitt Wallet",
        videoThumb: "/images/tutorials/fund-wallet.png",
        content: [
          "Your Baskitt Wallet is the central hub for all your transactions. Fund it once and use it across thousands of global retailers without worrying about currency conversion.",
          [
            "Go to 'Wallet' in the app menu.",
            "Tap 'Add Funds' and choose your preferred payment method.",
            "Enter the amount and confirm the transaction.",
            "Funds are typically reflected within minutes.",
          ],
          "Baskitt supports multiple payment methods including debit/credit cards, bank transfers, and select mobile money platforms depending on your region.",
          "Once funded, browse any supported retailer, add items to your cart, and check out using your Baskitt Wallet balance. Exchange rates are locked at the time of purchase for full transparency.",
        ],
      },
    ],
  },
  {
    id: "refund-method",
    title: "Refund Method",
    items: [
      {
        id: "refund-method-video",
        videoTitle: "How Refunds Work on Baskitt",
        videoThumb: "/images/tutorials/refund.png",
        content: [
          "We understand that sometimes purchases don't go as planned. Baskitt makes the refund process straightforward and transparent.",
          [
            "Navigate to 'Orders' in the app.",
            "Select the order you'd like to refund.",
            "Tap 'Request Refund' and select the reason.",
            "Submit and await confirmation from our team.",
          ],
          "Refunds are typically processed within 5–10 business days, depending on the retailer's policy and your payment method. Wallet refunds are usually faster than card refunds.",
          "If your refund request is denied by the retailer, our support team will review the case and work on your behalf to reach a resolution.",
          "For full details, please refer to our Refund Policy page on the website.",
        ],
      },
    ],
  },
  {
    id: "shipping-costs",
    title: "Shipping Costs",
    items: [
      {
        id: "shipping-costs-video",
        videoTitle: "Understanding Baskitt Shipping Fees",
        videoThumb: "/images/tutorials/shipping.png",
        content: [
          "Shipping costs on Baskitt are calculated based on the weight and dimensions of your package, the destination country, and your chosen shipping speed.",
          [
            "Standard shipping: 10–21 business days.",
            "Express shipping: 5–10 business days.",
            "Priority shipping: 2–5 business days (select regions).",
          ],
          "You'll see an estimated shipping cost during checkout before you confirm your order. No hidden fees — what you see is what you pay.",
          "Duties and import taxes may apply depending on your country's customs regulations. These are the buyer's responsibility and are not included in the Baskitt shipping fee.",
          "Use our Shipping Calculator on the website to get an estimate before you start shopping.",
        ],
      },
    ],
  },
  {
    id: "fraud-prevention",
    title: "Fraud Prevention & Policy Abuse",
    items: [
      {
        id: "fraud-prevention-video",
        videoTitle: "Staying Safe on Baskitt",
        videoThumb: "/images/tutorials/fraud.png",
        content: [
          "Baskitt takes security seriously. We employ multiple layers of fraud detection to protect both buyers and the platform from abuse.",
          [
            "Never share your account credentials or OTP with anyone.",
            "Baskitt will never ask for your password via email or chat.",
            "Report suspicious activity immediately via the app.",
            "Use only official Baskitt channels for payments.",
          ],
          "Policy abuse — including submitting false refund claims, manipulating promotional offers, or creating multiple accounts to circumvent restrictions — will result in account suspension.",
          "If your account is flagged or suspended, you'll receive a notification with details. You may appeal via our support team within 14 days of the suspension notice.",
          "For a full list of prohibited activities, please review our Terms of Service.",
        ],
      },
    ],
  },
];

export default function Tutorials() {
  const [active, setActive] = useState("");

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150;

      tutorialsData.forEach((section) => {
        const firstItem = document.getElementById(section.items[0].id);
        const lastItem = document.getElementById(
          section.items[section.items.length - 1].id
        );

        if (!firstItem || !lastItem) return;

        const top = firstItem.offsetTop;
        const bottom = lastItem.offsetTop + lastItem.offsetHeight;

        if (scrollPosition >= top && scrollPosition < bottom) {
          setActive(section.id);
        }
      });
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const renderContent = (content) => {
    if (typeof content === "string") return <p>{content}</p>;

    if (Array.isArray(content)) {
      return content.map((item, index) => {
        if (Array.isArray(item)) {
          return (
            <ul key={index}>
              {item.map((li, i) => (
                <li key={i}>{li}</li>
              ))}
            </ul>
          );
        }
        return <p key={index}>{item}</p>;
      });
    }
  };

  return (
    <>
      <div className="section navbar_section">
        <Navbar />
      </div>

      {/* HEADER */}
      <section id="tutorials_header">
        <h1>
          Video <span>Tutorials</span>
        </h1>

        <div className="box_image">
          <Image
            src="/images/tutorial.png"
            className="tutorials"
            width={110}
            height={110}
            alt="tutorials"
          />
        </div>
      </section>

      {/* TUTORIALS CONTENT */}
      <section id="tutorials_section">
        <div className="tutorials_wrapper">

          {/* SIDEBAR */}
          <div className="tutorials_sidebar">
            <ul>
              {tutorialsData.map((section, index) => (
                <li key={section.id}>
                  <a
                    href={`#${section.items[0].id}`}
                    className={active === section.id ? "active" : ""}
                  >
                    Step {index + 1}: {section.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* CONTENT */}
          <div className="tutorials_content">
            {tutorialsData.map((section, sectionIndex) => (
              <div key={section.id}>
                <h2>
                  Step {sectionIndex + 1}. {section.title}
                </h2>

                {section.items.map((item) => (
                  <section key={item.id} id={item.id}>
                    {/* VIDEO CARD */}
                    <div className="video_card">
                      <div className="video_thumb">
                        {item.videoThumb ? (
                          <Image
                            src={item.videoThumb}
                            alt={item.videoTitle}
                            fill
                            style={{ objectFit: "cover" }}
                          />
                        ) : (
                          <div className="video_thumb_placeholder" />
                        )}
                        <div className="play_btn">
                          <span className="play_icon" />
                        </div>
                      </div>
                      <div className="video_info">
                        <h3>{item.videoTitle}</h3>
                        <p>{item.content[0]}</p>
                      </div>
                    </div>

                    {/* REST OF CONTENT (skip first paragraph — shown in card) */}
                    {renderContent(item.content.slice(1))}
                  </section>
                ))}
              </div>
            ))}
          </div>

        </div>
      </section>

      <Footer />
    </>
  );
}